import { AddDeploymentRingModal } from "modules/field-mgmt/components/AddDeploymentRingModal";
import { useState } from "react";

import {
  confirm,
  DefaultButton,
  Heading,
  ShimmeredDetailsListProps,
  Stack,
  useTheme
} from "@bps/fluent-ui";
import { usePermissionsCheckQuery } from "@libs/api/gateways/env/env-gateway.hooks";
import {
  FieldDeviceRoleType,
  TenantDeploymentRingSummary
} from "@libs/api/gateways/field/field-ops-gateway.dtos";
import {
  FieldOpsCacheKeys,
  useCreateTenantDeploymentRings,
  useDeleteTenantDeploymentRings
} from "@libs/api/gateways/field/field-ops-gateway.hooks";
import { Tenant } from "@libs/api/gateways/plt/plt-gateway.dtos";
import { NO_ACTION_PERMISSION } from "@libs/permissions/permissions.constants";
import { Permissions } from "@libs/permissions/permissions.enum";

import { TenantDeploymentRing } from "./TenantDeploymentRingTable";

interface BaseTenantDeplomentRingsProps extends ShimmeredDetailsListProps {
  heading: string;
  tenant: Tenant;
  fieldDeviceRoleType: FieldDeviceRoleType;
}

export const BaseTenantDeplomentRings = ({
  heading,
  items,
  tenant,
  fieldDeviceRoleType,
  ...props
}: BaseTenantDeplomentRingsProps) => {
  const theme = useTheme();
  const [showAddFieldDevice, setShowAddFieldDevice] = useState<boolean>(false);
  const {
    mutateAsync: addTenantDeploymentRing
  } = useCreateTenantDeploymentRings();

  const {
    mutateAsync: removeTenantDeploymentRing
  } = useDeleteTenantDeploymentRings();

  const { data: hasWritePermission } = usePermissionsCheckQuery(
    Permissions.PltFieldManagementWrite
  );

  const onRemoveItem = async (
    TenantDeploymentRingSummary: TenantDeploymentRingSummary
  ) => {
    const isConfirmed = await confirm({
      confirmButtonProps: {
        text: "Remove"
      },
      cancelButtonProps: {
        text: "Cancel"
      },
      dialogContentProps: {
        title: `Remove client deployment ring ${TenantDeploymentRingSummary.deploymentRingCode}?`,
        subText: `Are you sure you want to remove the deployment ring from this tenant's ${fieldDeviceRoleType.toLocaleLowerCase()} machines?`
      }
    });
    if (isConfirmed) {
      removeTenantDeploymentRing(TenantDeploymentRingSummary.id);
    }
  };

  const onAddItem = async () => {
    setShowAddFieldDevice(!showAddFieldDevice);
  };

  const reOrderedItems = (items as TenantDeploymentRingSummary[]).sort(
    (a, b) => a.sortOrder - b.sortOrder
  );

  return (
    <Stack
      styles={{
        root: {
          height: 400
        }
      }}
      tokens={{ childrenGap: theme.spacing.s1 }}
    >
      <Stack horizontal styles={{ root: { justifyContent: "space-between" } }}>
        <Heading variant="section-heading">{heading}</Heading>
        <Stack.Item>
          <DefaultButton
            disabled={!hasWritePermission}
            title={!hasWritePermission ? NO_ACTION_PERMISSION : undefined}
            iconProps={{ iconName: "Add" }}
            onClick={() => onAddItem()}
          >
            <b>Add {heading}</b>
          </DefaultButton>
        </Stack.Item>
      </Stack>
      <Stack.Item
        verticalFill
        styles={{
          root: {
            position: "relative",
            height: "90%",
            flex: "1 1 0",
            overflowY: "auto"
          }
        }}
      >
        <TenantDeploymentRing
          onRemoveItem={onRemoveItem}
          onAddItem={onAddItem}
          heading={heading}
          items={reOrderedItems}
          {...props}
        />
      </Stack.Item>

      <AddDeploymentRingModal
        show={showAddFieldDevice}
        title={`Add ${heading}`}
        existingDeploymentRingIds={
          reOrderedItems?.map(r => r.deploymentRingId) ?? []
        }
        refreshCacheKey={FieldOpsCacheKeys.TenantDeploymentRings}
        onDismiss={() => setShowAddFieldDevice(false)}
        addDeploymentRing={async ring =>
          await addTenantDeploymentRing({
            tenantId: tenant.id,
            fieldDeviceRoleTypeSid: fieldDeviceRoleType,
            deploymentRingId: ring.id,
            sortOrder: 0
          })
        }
      />
    </Stack>
  );
};
